import { FC } from 'react';
import styles from './bt-sparkline.module.scss';

type Props = {
  values: number[];
  min: number;
  max: number;
  gridLines?: number[];
  startLabel?: string;
  endLabel?: string;
  width?: number;
  height?: number;
  fluid?: boolean;
};

const PADDING = 4;

const BtSparkline: FC<Props> = ({
  values,
  min,
  max,
  gridLines = [],
  startLabel,
  endLabel,
  width = 160,
  height = 40,
  fluid = false,
}) => {
  if (!values.length) return null;

  const range = max - min || 1;
  const yOf = (value: number) => {
    const clamped = Math.min(max, Math.max(min, value));
    return PADDING + ((max - clamped) / range) * (height - 2 * PADDING);
  };
  const step = values.length > 1 ? (width - 2 * PADDING) / (values.length - 1) : 0;
  const xOf = (index: number) => (values.length > 1 ? PADDING + index * step : width / 2);
  const points = values.map((value, index) => ({ x: xOf(index), y: yOf(value) }));

  return (
    <div className={styles.sparkline} style={{ width: fluid ? '100%' : width }}>
      <svg
        width={fluid ? '100%' : width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio={fluid ? 'xMinYMid meet' : undefined}
        aria-hidden="true"
      >
        {gridLines.map((value, index) => (
          <line
            key={index}
            className={styles.grid}
            x1={0}
            x2={width}
            y1={yOf(value)}
            y2={yOf(value)}
          />
        ))}
        <polyline className={styles.line} points={points.map(p => `${p.x},${p.y}`).join(' ')} />
        {points.map(point => (
          <circle key={point.x} className={styles.point} cx={point.x} cy={point.y} r={2.5} />
        ))}
      </svg>
      {(startLabel || endLabel) && (
        <div className={styles.labels}>
          <span>{startLabel}</span>
          <span>{endLabel}</span>
        </div>
      )}
    </div>
  );
};

export default BtSparkline;
